'use client';

import { useCallback } from 'react';
import { useMultistepFormNavigation } from './useMultistepFormNavigation';

type UseMultistepFormStepOptions = {
  /**
   * Callback function when the step is submitted successfully.
   * Return false to prevent navigation to the next step.
   */
  onStepSubmit?: () => boolean | Promise<boolean>;

  /**
   * Whether to automatically navigate to the next step after successful submission.
   * @default true
   */
  autoNavigate?: boolean;
};

/**
 * Hook for managing individual step logic within a multistep form.
 * Provides helpers for form submission and navigation control.
 *
 * @param options - Configuration options for the step
 * @returns Helper functions and state for the step
 *
 * @example
 * ```tsx
 * // Step with automatic navigation after validation
 * function EmailStep() {
 *   const [email, setEmail] = useState('');
 *   const { handleStepSubmit, isFirstStep, isLastStep, goToPreviousStep } = useMultistepFormStep({
 *     onStepSubmit: async () => {
 *       // Validate email
 *       if (!email.includes('@')) {
 *         alert('Invalid email');
 *         return false; // Prevent navigation
 *       }
 *       return true; // Allow navigation
 *     }
 *   });
 *
 *   return (
 *     <form onSubmit={handleStepSubmit}>
 *       <input
 *         type="email"
 *         value={email}
 *         onChange={(e) => setEmail(e.target.value)}
 *         required
 *       />
 *       <button type="submit">Continue</button>
 *       {!isFirstStep && (
 *         <button type="button" onClick={goToPreviousStep}>Back</button>
 *       )}
 *     </form>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Step with manual navigation control
 * function UploadStep() {
 *   const [files, setFiles] = useState<File[]>([]);
 *   const { handleStepSubmit, isLoading, setIsLoading } = useMultistepFormStep({
 *     onStepSubmit: async () => {
 *       setIsLoading(true);
 *       try {
 *         await uploadFiles(files);
 *         return true; // Success - navigate to next step
 *       } catch (error) {
 *         alert('Upload failed');
 *         return false; // Error - stay on current step
 *       } finally {
 *         setIsLoading(false);
 *       }
 *     }
 *   });
 *
 *   return (
 *     <form onSubmit={handleStepSubmit}>
 *       <input type="file" onChange={(e) => setFiles([...e.target.files])} />
 *       <button type="submit" disabled={isLoading}>
 *         {isLoading ? 'Uploading...' : 'Upload'}
 *       </button>
 *     </form>
 *   );
 * }
 * ```
 */
function useMultistepFormStep(options: UseMultistepFormStepOptions = {}) {
  const { onStepSubmit, autoNavigate = true } = options;
  const navigation = useMultistepFormNavigation();

  const handleStepSubmit = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault();

      // If there's a custom submit handler, call it
      if (onStepSubmit) {
        const shouldProceed = await onStepSubmit();
        if (!shouldProceed) {
          return;
        }
      }

      // Automatically navigate to next step if enabled and not on last step
      if (autoNavigate && !navigation.isLastStep) {
        await navigation.goToNextStep();
      }
    },
    [onStepSubmit, autoNavigate, navigation]
  );

  return {
    ...navigation,
    handleStepSubmit,
  };
}

export { useMultistepFormStep };
